import { query } from "../../../erd-editor-schema/dist/query/index.js";
import { arrayHas } from "../../../utils/arrayHas.js";
import { canvasSizeInRange, textInRange } from "../../../utils/validation.js";
import { createAction } from "../../../r-html/dist/store/index.js";
import { createTable } from "../../../utils/collection/table.entity.js";
import { POSITION_REGISTER, isOwnStep } from "../../geometryRegisters.js";
import { calcTableHeight, calcTableWidths } from "../../../utils/calcTable.js";
import { unionRect } from "../../../konva/scene/metrics.js";
import { getTableGroupId, padRect } from "../../../utils/tableGroup.js";
import { ActionType } from "./actions.js";
import { round } from "es-toolkit/compat";
//#region src/engine/modules/table/atom.actions.ts
var addTableAction = createAction(ActionType.addTable);
var addTable = ({ doc, collections, lww }, { payload: { id, ui }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	query(collections).collection("tableEntities").addOne(createTable({
		id,
		ui
	})).addOperator(lww, safeVersion, id, () => {
		if (!arrayHas(doc.tableIds)(id)) doc.tableIds.push(id);
	});
};
var moveTableAction = createAction(ActionType.moveTable);
var moveTable = ({ collections, lww }, { payload: { ids, movementX, movementY }, version, tags }, context) => {
	const collection = query(collections).collection("tableEntities");
	for (const id of ids) collection.getOrCreate(id, (id) => createTable({ id }));
	const step = (table) => {
		table.ui.x = round(table.ui.x + movementX, 4);
		table.ui.y = round(table.ui.y + movementY, 4);
	};
	if (!isOwnStep(context, tags)) {
		collection.updateMany(ids, step);
		return;
	}
	const safeVersion = version ?? context.clock.getVersion();
	for (const id of ids) collection.replaceOperator(lww, safeVersion, id, POSITION_REGISTER, () => {
		collection.updateOne(id, step);
	});
};
var moveToTableAction = createAction(ActionType.moveToTable);
var moveToTable = ({ collections, lww }, { payload: { id, x, y }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableEntities");
	collection.getOrCreate(id, (id) => createTable({ id }));
	collection.replaceOperator(lww, safeVersion, id, POSITION_REGISTER, () => {
		collection.updateOne(id, (table) => {
			table.ui.x = x;
			table.ui.y = y;
		});
	});
};
var removeTableAction = createAction(ActionType.removeTable);
var removeTable = ({ doc, collections, lww }, { payload: { id }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	query(collections).collection("tableEntities").removeOperator(lww, safeVersion, id, () => {
		const index = doc.tableIds.indexOf(id);
		if (index !== -1) doc.tableIds.splice(index, 1);
	});
};
var changeTableNameAction = createAction(ActionType.changeTableName);
var changeTableName = ({ collections, lww }, { payload: { id, value }, version }, { toWidth, clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableEntities");
	collection.getOrCreate(id, (id) => createTable({ id }));
	collection.replaceOperator(lww, safeVersion, id, "name", () => {
		collection.updateOne(id, (table) => {
			table.name = value;
			table.ui.widthName = textInRange(toWidth(value));
		});
	});
};
var changeTableLogicalNameAction = createAction(ActionType.changeTableLogicalName);
var changeTableLogicalName = ({ collections, lww }, { payload: { id, value }, version }, { toWidth, clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableEntities");
	collection.getOrCreate(id, (id) => createTable({ id }));
	collection.replaceOperator(lww, safeVersion, id, "logicalName", () => {
		collection.updateOne(id, (table) => {
			table.logicalName = value;
			table.ui.widthLogicalName = textInRange(toWidth(value));
		});
	});
};
var changeTableNameDerivedAction = createAction(ActionType.changeTableNameDerived);
var changeTableNameDerived = ({ collections, lww }, { payload: { id, value }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableEntities");
	collection.getOrCreate(id, (id) => createTable({ id }));
	collection.replaceOperator(lww, safeVersion, id, "nameDerived", () => {
		collection.updateOne(id, (table) => {
			table.nameDerived = value;
		});
	});
};
var changeTableCommentAction = createAction(ActionType.changeTableComment);
var changeTableComment = ({ collections, lww }, { payload: { id, value }, version }, { toWidth, clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableEntities");
	collection.getOrCreate(id, (id) => createTable({ id }));
	collection.replaceOperator(lww, safeVersion, id, "comment", () => {
		collection.updateOne(id, (table) => {
			table.comment = value;
			table.ui.widthComment = textInRange(toWidth(value));
		});
	});
};
var changeTableColorAction = createAction(ActionType.changeTableColor);
var changeTableColor = ({ collections, lww }, { payload: { id, color }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableEntities");
	collection.getOrCreate(id, (id) => createTable({ id }));
	collection.replaceOperator(lww, safeVersion, id, "ui.color", () => {
		collection.updateOne(id, (table) => {
			table.ui.color = color;
		});
	});
};
var changeTableGroupAction = createAction(ActionType.changeTableGroup);
var changeTableGroup = ({ collections, lww }, { payload: { id, value }, version }, { clock }) => {
	const safeVersion = version ?? clock.getVersion();
	const collection = query(collections).collection("tableEntities");
	collection.getOrCreate(id, (id) => createTable({ id }));
	collection.replaceOperator(lww, safeVersion, id, "groupId", () => {
		collection.updateOne(id, (table) => {
			table.groupId = value;
		});
	});
};
var changeZIndexAction = createAction(ActionType.changeZIndex);
var changeZIndex = ({ collections }, { payload: { id, zIndex } }) => {
	const collection = query(collections).collection("tableEntities");
	collection.getOrCreate(id, (id) => createTable({ id }));
	collection.updateOne(id, (table) => {
		table.ui.zIndex = zIndex;
	});
};
var sortTableAction = createAction(ActionType.sortTable);
function placeInRows(cells, start, width, rowHeight) {
	let widthSum = start;
	let currentHeight = start;
	let maxHeight = rowHeight;
	cells.forEach((cell) => {
		const cellWidth = cell.width + 80;
		const cellHeight = cell.height + 80;
		if (widthSum + cellWidth > width) {
			currentHeight += maxHeight;
			maxHeight = 0;
			widthSum = start;
		}
		if (maxHeight < cellHeight) maxHeight = cellHeight;
		cell.place(widthSum, currentHeight);
		widthSum += cellWidth;
	});
}
var tableCell = (state, table) => ({
	width: calcTableWidths(table, state).width,
	height: calcTableHeight(table),
	place: (x, y) => {
		table.ui.x = x;
		table.ui.y = y;
	}
});
var sortWidth = ({ doc }) => canvasSizeInRange(doc.tableIds.length * 100);
function groupCell(state, groupId, members) {
	const rects = [];
	placeInRows(members.map((member, index) => ({
		...member,
		place: (x, y) => {
			rects[index] = {
				x,
				y,
				width: member.width,
				height: member.height
			};
		}
	})), 0, sortWidth(state) - 50 - 48, 0);
	const box = padRect(rects.reduce(unionRect));
	return {
		width: box.width,
		height: box.height,
		place: (x, y) => {
			members.forEach((member, index) => member.place(x + rects[index].x - box.x, y + rects[index].y - box.y));
			query(state.collections).collection("tableGroupEntities").updateOne(groupId, (group) => {
				group.ui.x = x;
				group.ui.y = y;
				group.ui.width = box.width;
				group.ui.height = box.height;
			});
		}
	};
}
function toSortCells(state, tables) {
	const membersByGroup = /* @__PURE__ */ new Map();
	const order = [];
	tables.forEach((table) => {
		const cell = tableCell(state, table);
		const groupId = getTableGroupId(state, table);
		const members = groupId ? membersByGroup.get(groupId) : void 0;
		if (!groupId) order.push(cell);
		else if (members) members.push(cell);
		else {
			membersByGroup.set(groupId, [cell]);
			order.push(groupId);
		}
	});
	return order.map((cell) => typeof cell === "string" ? groupCell(state, cell, membersByGroup.get(cell)) : cell);
}
var sortTable = (state) => {
	const { doc, collections } = state;
	const tables = query(collections).collection("tableEntities").selectByIds(doc.tableIds);
	tables.sort((a, b) => a.columnIds.length - b.columnIds.length);
	placeInRows(toSortCells(state, tables), 50, sortWidth(state), 50);
};
var tableReducers = {
	[ActionType.addTable]: addTable,
	[ActionType.moveTable]: moveTable,
	[ActionType.moveToTable]: moveToTable,
	[ActionType.removeTable]: removeTable,
	[ActionType.changeTableName]: changeTableName,
	[ActionType.changeTableComment]: changeTableComment,
	[ActionType.changeTableColor]: changeTableColor,
	[ActionType.changeTableGroup]: changeTableGroup,
	[ActionType.changeZIndex]: changeZIndex,
	[ActionType.sortTable]: sortTable,
	[ActionType.changeTableLogicalName]: changeTableLogicalName,
	[ActionType.changeTableNameDerived]: changeTableNameDerived
};
var actions = {
	addTableAction,
	moveTableAction,
	moveToTableAction,
	removeTableAction,
	changeTableNameAction,
	changeTableCommentAction,
	changeTableColorAction,
	changeTableGroupAction,
	changeZIndexAction,
	sortTableAction,
	changeTableLogicalNameAction,
	changeTableNameDerivedAction
};
//#endregion
export { actions, addTableAction, changeTableColorAction, changeTableCommentAction, changeTableGroupAction, changeTableLogicalNameAction, changeTableNameAction, changeTableNameDerivedAction, changeZIndexAction, moveTableAction, moveToTableAction, removeTableAction, sortTableAction, tableReducers };
